import {
  Box,
  VStack,
  Heading,
  Text,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Badge,
  Flex,
  Icon,
} from "@chakra-ui/react"
import { FaBook, FaHeart, FaBrain } from "react-icons/fa6"
import { useAppSelector } from "../../app/hooks"
import {
  selectGameAge,
  selectGameEducation,
  selectGameMentalHealth,
  selectGamePhysicalHealth,
  selectHistory,
  selectTurn,
} from "./gameSlice"
import { ResetButton } from "./SlideViewer"

export const GameSummary = () => {
  const age = useAppSelector(selectGameAge)
  const education = useAppSelector(selectGameEducation)
  const mentalHealth = useAppSelector(selectGameMentalHealth)
  const physicalHealth = useAppSelector(selectGamePhysicalHealth)
  const history = useAppSelector(selectHistory)
  const turns = useAppSelector(selectTurn)

  // Find best and worst choices based on total impact
  const choicesWithImpact = history.filter(h => h.totalImpact !== undefined)
  const bestChoice =
    choicesWithImpact.length > 0
      ? choicesWithImpact.reduce((prev, current) =>
          (current.totalImpact ?? 0) > (prev.totalImpact ?? 0) ? current : prev,
        )
      : null

  const worstChoice =
    choicesWithImpact.length > 0
      ? choicesWithImpact.reduce((prev, current) =>
          (current.totalImpact ?? 0) < (prev.totalImpact ?? 0) ? current : prev,
        )
      : null

  const totalScore = education + mentalHealth + physicalHealth

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, #7c3aed, #5b4dd6)"
      p={{ base: 4, md: 6, lg: 8 }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box maxW="1200px" w="100%">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Heading size="2xl" mb={2} color="white">
              Game Over
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.900">
              Your Journey Summary
            </Text>
          </Box>

          {/* Final Stats Card */}
          <Card borderRadius="2xl" boxShadow="2xl" bg="white">
            <CardHeader
              bg="purple.50"
              borderBottom="1px"
              borderColor="purple.100"
              borderTopRadius="2xl"
            >
              <Heading size="md" color="purple.700">
                Final Statistics
              </Heading>
            </CardHeader>
            <CardBody>
              <Grid
                templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                gap={4}
              >
                <GridItem>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={FaBook} color="red.300" />
                    <Text fontWeight="bold">Education</Text>
                  </Flex>
                  <Text fontSize="2xl">{education}</Text>
                </GridItem>

                <GridItem>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={FaBrain} color="blue.500" />
                    <Text fontWeight="bold">Mental Health</Text>
                  </Flex>
                  <Text fontSize="2xl">{mentalHealth}</Text>
                </GridItem>

                <GridItem>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={FaHeart} color="green.500" />
                    <Text fontWeight="bold">Physical Health</Text>
                  </Flex>
                  <Text fontSize="2xl">{physicalHealth}</Text>
                </GridItem>
              </Grid>

              <Divider my={4} />

              <Grid
                templateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                gap={4}
              >
                <GridItem>
                  <Text color="gray.600" fontSize="sm">
                    Age Reached
                  </Text>
                  <Text fontSize="xl" fontWeight="bold">
                    {age} years
                  </Text>
                </GridItem>
                <GridItem>
                  <Text color="gray.600" fontSize="sm">
                    Choices Made
                  </Text>
                  <Text fontSize="xl" fontWeight="bold">
                    {turns}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text color="gray.600" fontSize="sm">
                    Total Score
                  </Text>
                  <Text fontSize="xl" fontWeight="bold">
                    {totalScore}
                  </Text>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* Best and Worst Choices */}
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
            {/* Best Choice */}
            {bestChoice && (
              <Card
                borderTop="4px"
                borderColor="green.500"
                borderRadius="2xl"
                boxShadow="xl"
                bg="white"
              >
                <CardHeader>
                  <Flex align="center" gap={2}>
                    <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                      Best Choice
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Scenario
                      </Text>
                      <Text fontWeight="bold">{bestChoice.slide}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Your Choice
                      </Text>
                      <Text fontWeight="bold" color="green.600">
                        {bestChoice.action}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Impact
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="green.600">
                        +{bestChoice.totalImpact}
                      </Text>
                    </Box>
                    {bestChoice.effects && (
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Effects
                        </Text>
                        <Flex wrap="wrap" gap={2}>
                          {bestChoice.effects.agency && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.agency > 0 ? "green" : "red"
                              }
                            >
                              Agency {bestChoice.effects.agency > 0 ? "+" : ""}
                              {bestChoice.effects.agency}
                            </Badge>
                          )}
                          {bestChoice.effects.education && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.education > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Education{" "}
                              {bestChoice.effects.education > 0 ? "+" : ""}
                              {bestChoice.effects.education}
                            </Badge>
                          )}
                          {bestChoice.effects.health && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.health > 0 ? "green" : "red"
                              }
                            >
                              Health {bestChoice.effects.health > 0 ? "+" : ""}
                              {bestChoice.effects.health}
                            </Badge>
                          )}
                          {bestChoice.effects.relationship && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.relationship > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Relationship{" "}
                              {bestChoice.effects.relationship > 0 ? "+" : ""}
                              {bestChoice.effects.relationship}
                            </Badge>
                          )}
                          {bestChoice.effects.mentalHealth && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.mentalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Mental{" "}
                              {bestChoice.effects.mentalHealth > 0 ? "+" : ""}
                              {bestChoice.effects.mentalHealth}
                            </Badge>
                          )}
                          {bestChoice.effects.physicalHealth && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.physicalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Physical{" "}
                              {bestChoice.effects.physicalHealth > 0 ? "+" : ""}
                              {bestChoice.effects.physicalHealth}
                            </Badge>
                          )}
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* Worst Choice */}
            {worstChoice && (
              <Card
                borderTop="4px"
                borderColor="red.500"
                borderRadius="2xl"
                boxShadow="xl"
                bg="white"
              >
                <CardHeader>
                  <Flex align="center" gap={2}>
                    <Badge colorScheme="red" fontSize="md" px={2} py={1}>
                      Most Challenging Choice
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Scenario
                      </Text>
                      <Text fontWeight="bold">{worstChoice.slide}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Your Choice
                      </Text>
                      <Text fontWeight="bold" color="red.600">
                        {worstChoice.action}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Impact
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="red.600">
                        {worstChoice.totalImpact}
                      </Text>
                    </Box>
                    {worstChoice.effects && (
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Effects
                        </Text>
                        <Flex wrap="wrap" gap={2}>
                          {worstChoice.effects.agency && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.agency > 0 ? "green" : "red"
                              }
                            >
                              Agency {worstChoice.effects.agency > 0 ? "+" : ""}
                              {worstChoice.effects.agency}
                            </Badge>
                          )}
                          {worstChoice.effects.education && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.education > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Education{" "}
                              {worstChoice.effects.education > 0 ? "+" : ""}
                              {worstChoice.effects.education}
                            </Badge>
                          )}
                          {worstChoice.effects.health && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.health > 0 ? "green" : "red"
                              }
                            >
                              Health {worstChoice.effects.health > 0 ? "+" : ""}
                              {worstChoice.effects.health}
                            </Badge>
                          )}
                          {worstChoice.effects.relationship && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.relationship > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Relationship{" "}
                              {worstChoice.effects.relationship > 0 ? "+" : ""}
                              {worstChoice.effects.relationship}
                            </Badge>
                          )}
                          {worstChoice.effects.mentalHealth && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.mentalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Mental{" "}
                              {worstChoice.effects.mentalHealth > 0 ? "+" : ""}
                              {worstChoice.effects.mentalHealth}
                            </Badge>
                          )}
                          {worstChoice.effects.physicalHealth && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.physicalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Physical{" "}
                              {worstChoice.effects.physicalHealth > 0
                                ? "+"
                                : ""}
                              {worstChoice.effects.physicalHealth}
                            </Badge>
                          )}
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            )}
          </Grid>

          {/* Reflection Section */}
          <Card bg="blue.50" borderRadius="2xl" boxShadow="xl">
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Heading size="md">Reflection Time</Heading>
                <Text>Take a moment to discuss with your mentor:</Text>
                <Box pl={4}>
                  <Text>• What choices led to your best outcomes?</Text>
                  <Text>• What would you do differently next time?</Text>
                  <Text>
                    • How do these choices relate to real-life decisions?
                  </Text>
                  <Text>
                    • What did you learn about the consequences of your actions?
                  </Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Play Again Button */}
          <ResetButton />
        </VStack>
      </Box>
    </Box>
  )
}
